/**
 * Side-by-side comparison of two immutable revisions (pure). Compares the normalized `content` of each
 * revision plus its role → asset assignment. Only persisted values are compared; nothing is inferred.
 */
export type Change = 'added' | 'removed' | 'changed' | 'same';
export type DiffGroup = 'identity' | 'domains' | 'metadata' | 'colors';

export interface FieldDiff {
  group: DiffGroup;
  field: string;
  before: string | null;
  after: string | null;
  change: Change;
}

export interface AssetDiff {
  role: string;
  before: { sha256: string; url: string | null; mime: string } | null;
  after: { sha256: string; url: string | null; mime: string } | null;
  change: Change;
}

export interface RevisionLike {
  content: Record<string, unknown>;
  assets: Array<{ role: string; sha256: string; mime: string; publicUrl: string | null }>;
}

type Rec = Record<string, unknown>;
const get = (o: Rec | undefined, path: string): unknown =>
  path
    .split('.')
    .reduce<unknown>((v, k) => (v && typeof v === 'object' ? (v as Rec)[k] : undefined), o);

/** Canonical, order-insensitive rendering of a value for comparison and display. */
export function show(v: unknown): string | null {
  if (v == null || v === '') return null;
  if (Array.isArray(v)) {
    if (!v.length) return null;
    return v
      .map((x) => (typeof x === 'object' ? JSON.stringify(x) : String(x)))
      .sort()
      .join(', ');
  }
  if (typeof v === 'object') {
    const entries = Object.entries(v as Rec).filter(([, x]) => x != null && x !== '');
    if (!entries.length) return null;
    return entries
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, x]) => `${k}: ${typeof x === 'object' ? JSON.stringify(x) : String(x)}`)
      .join('; ');
  }
  return String(v);
}

const cmp = (before: string | null, after: string | null): Change =>
  before === after ? 'same' : before == null ? 'added' : after == null ? 'removed' : 'changed';

const FIELDS: Array<[DiffGroup, string]> = [
  ['identity', 'identity.name'],
  ['identity', 'identity.kind'],
  ['identity', 'identity.localizedNames'],
  ['identity', 'identity.aliases'],
  ['metadata', 'classification.region'],
  ['metadata', 'classification.regionSource'],
  ['metadata', 'classification.category'],
  ['metadata', 'classification.country'],
  ['metadata', 'classification.city'],
  ['metadata', 'classification.industries'],
  ['metadata', 'description'],
  ['metadata', 'longDescription'],
  ['metadata', 'company'],
  ['metadata', 'socialLinks'],
  ['metadata', 'fonts'],
  ['metadata', 'provider.brandfetchId'],
  ['colors', 'colors.primary'],
];

export function diffRevisions(
  a: RevisionLike,
  b: RevisionLike,
): { fields: FieldDiff[]; assets: AssetDiff[] } {
  const fields: FieldDiff[] = FIELDS.map(([group, field]) => {
    const before = show(get(a.content, field));
    const after = show(get(b.content, field));
    return { group, field, before, after, change: cmp(before, after) };
  });
  // Domains and palette: per-member presence (a changed role/verification shows as removed + added).
  const domainKey = (d: Rec) => `${d.domain} (${d.role}, ${d.verification})`;
  const setDiff = (group: DiffGroup, field: string, xs: string[], ys: string[]) => {
    for (const x of [...new Set([...xs, ...ys])].sort()) {
      const inA = xs.includes(x);
      const inB = ys.includes(x);
      fields.push({
        group,
        field,
        before: inA ? x : null,
        after: inB ? x : null,
        change: inA && inB ? 'same' : inA ? 'removed' : 'added',
      });
    }
  };
  setDiff(
    'domains',
    'domains',
    ((a.content.domains as Rec[] | undefined) ?? []).map(domainKey),
    ((b.content.domains as Rec[] | undefined) ?? []).map(domainKey),
  );
  setDiff(
    'colors',
    'colors.palette',
    ((get(a.content, 'colors.palette') as string[] | undefined) ?? []).map((c) => c.toLowerCase()),
    ((get(b.content, 'colors.palette') as string[] | undefined) ?? []).map((c) => c.toLowerCase()),
  );
  const roles = [
    ...new Set([...a.assets.map((x) => x.role), ...b.assets.map((x) => x.role)]),
  ].sort();
  const assets: AssetDiff[] = roles.map((role) => {
    const x = a.assets.find((r) => r.role === role);
    const y = b.assets.find((r) => r.role === role);
    const pack = (r: typeof x) => (r ? { sha256: r.sha256, url: r.publicUrl, mime: r.mime } : null);
    return {
      role,
      before: pack(x),
      after: pack(y),
      change: cmp(x?.sha256 ?? null, y?.sha256 ?? null),
    };
  });
  return { fields, assets };
}
